import type { ChartOverlay } from "components/charts/modern/overlay/types";
import { OverlayDataKind } from "components/charts/modern/overlay/types";
import useRootIssuer from "components/charts/modern/overlay/useRootIssuer";
import { differenceInYears, getYear } from "date-fns";
import isWeekend from "date-fns/isWeekend";
import nextMonday from "date-fns/nextMonday";
import parseISO from "date-fns/parseISO";
import { useState } from "react";
import { useIntl } from "react-intl";
import colors from "tailwindcss/colors";

interface UseChartOverlaysReturnProps {
  overlays: ChartOverlay[];
  selectedOverlayKind: OverlayDataKind | null;
  setSelectedOverlayKind: (kind: OverlayDataKind | null) => void;
}


export default function useChartOverlays(): UseChartOverlaysReturnProps {
  const issuer = useRootIssuer();
  const intl = useIntl();

  const [selectedOverlayKind, setSelectedOverlayKind] = useState<OverlayDataKind | null>(null);

  if (issuer.__typename === "BrazilianRealEstateFund") {
    return {
      overlays: [],
      selectedOverlayKind,
      setSelectedOverlayKind,
    };
  }

  const overlays = [
    {
      kind: OverlayDataKind.MarketCrisis,
      title: intl.formatMessage({ defaultMessage: "Crises Globais", id: "ROeE3B" }),
      description: intl.formatMessage({ defaultMessage: "Crises que afetaram os mercados", id: "yRyJ1k" }),
      data: GLOBAL_IMPACTFUL_MARKET_EVENTS.map((node) => {
        const reportDate = node.startDate;
        const diffInYears = differenceInYears(node.endDate, node.startDate);
        return {
          x: isWeekend(reportDate) ? nextMonday(reportDate) : reportDate,
          label: node.name.en,
          title: "C",
          time:
            diffInYears < 1
              ? `${getYear(node.startDate)}`
              : `${getYear(node.startDate)} → ${getYear(node.endDate)} (${diffInYears} years)`,
          theme: {
            backgroundColor: colors.pink[50],
            textColor: colors.pink[600],
          },
        };
      }),
    },
  ].filter((node) => {
    return node.data.length > 0;
  });

  return {
    overlays,
    selectedOverlayKind,
    setSelectedOverlayKind,
  };
}

export function isEarningOverlayKind<CO extends ChartOverlay, K extends CO["kind"]>(kind: K): kind is K {
  return [
    OverlayDataKind.QuarterlyEarnings,
    OverlayDataKind.QuarterlyLosses,
    OverlayDataKind.YearlyEarnings,
    OverlayDataKind.YearlyLosses,
  ].includes(kind);
}

const GLOBAL_IMPACTFUL_MARKET_EVENTS = [
  {
    name: {
      pt: "Crise do Petróleo",
      en: "Oil Crisis",
    },
    startDate: parseISO("1973-10-17"),
    endDate: parseISO("1979-12-31"),
  },
  {
    name: {
      pt: "Segunda-Feira Negra",
      en: "Black Monday",
    },
    startDate: parseISO("1987-10-19"),
    endDate: parseISO("1987-10-19"),
  },
  {
    name: {
      pt: "Bolha das Empresas de Tecnologia",
      en: "Dot-com Bubble",
    },
    startDate: parseISO("2000-03-10"),
    endDate: parseISO("2000-03-10"),
  },
  {
    name: {
      pt: "Crise Financeira Asiática",
      en: "Asian Financial Crisis",
    },
    startDate: parseISO("1997-07-02"),
    endDate: parseISO("1998-02-01"),
  },
  {
    name: {
      pt: "Crise Financeira Global",
      en: "Global Financial Crisis",
    },
    startDate: parseISO("2007-12-01"),
    endDate: parseISO("2009-06-30"),
  },
  {
    name: {
      pt: "Crise da Dívida da Zona Euro",
      en: "Eurozone Debt Crisis",
    },
    startDate: parseISO("2010-10-01"),
    endDate: parseISO("2014-12-31"),
  },
  {
    name: {
      pt: "Boom do Bitcoin e das Criptomoedas",
      en: "Bitcoin and Cryptocurrency Boom",
    },
    startDate: parseISO("2017-01-01"),
    endDate: parseISO("2018-12-31"),
  },
  {
    name: {
      pt: "Pandemia da COVID-19",
      en: "COVID-19 Pandemic",
    },
    startDate: parseISO("2020-01-01"),
    endDate: parseISO("2022-01-01"),
  },
  {
    name: {
      pt: "Crise da Dívida Grega",
      en: "Greek Debt Crisis",
    },
    startDate: parseISO("2010-04-23"),
    endDate: parseISO("2015-08-20"),
  },
  {
    name: {
      pt: "Brexit",
      en: "Brexit",
    },
    startDate: parseISO("2016-06-23"),
    endDate: parseISO("2020-01-31"),
  },
  {
    name: {
      pt: "Crise Financeira do Subprime",
      en: "Subprime Mortgage Crisis",
    },
    startDate: parseISO("2007-06-01"),
    endDate: parseISO("2009-12-31"),
  },
];
